import { analytics } from '../analytics/client';
import type { ApplicationAccess } from './subscription';

export const SUBSCRIPTION_ACCESS_EVENT = 'aydentify:subscription-access';
let subscriptionAccess: ApplicationAccess | undefined;
let accessReceivedAt = Date.now();
export const getSubscriptionAccess = () => subscriptionAccess;
export const getAccessReceivedAt = () => accessReceivedAt;

export function setSubscriptionAccess(access: ApplicationAccess | undefined, redirect = true) {
  if (!access) return; // Compatibility while the frontend is rolled out before the API.
  if (subscriptionAccess?.allowed !== access.allowed && (subscriptionAccess || !access.allowed)) generation++;
  subscriptionAccess = access;
  accessReceivedAt = Date.now();
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(SUBSCRIPTION_ACCESS_EVENT, { detail: access }));
  if (!access.allowed && redirect) navigate('/subscription-required');
}

export function assertCurrentAccessResponse(response: any) {
  const requestGeneration = response.config?.__accessGeneration;
  if (requestGeneration !== undefined && requestGeneration !== generation) {
    const error = new Error('The access session has changed');
    Object.assign(error, { name: 'CanceledError', code: 'ERR_CANCELED' });
    throw error;
  }
  return response;
}

export const ACCOUNT_DISABLED_EVENT = 'aydentify:account-disabled';
export const ACCOUNT_DISABLED_MESSAGE = 'Your account has been disabled. Please contact support.';
let generation = 0;
let userId: string | undefined;
let disabled = false;
let navigate = (path: string) => {
  if (typeof window !== 'undefined') window.location.replace(path);
};

// Registered by app.tsx, avoiding a dependency cycle through Umi's app exports.
export function configureAccessNavigation(handler: (path: string) => void) {
  navigate = handler;
}

export function advanceAccessSession() {
  generation++;
  disabled = false;
  subscriptionAccess = undefined;
}
export function setAccessIdentity(id: unknown) {
  const next = id === undefined || id === null ? undefined : String(id);
  if (next !== userId) {
    userId = next;
    advanceAccessSession();
  }
}

function applicationUrl(url: string, apiBase: string): URL | undefined {
  try {
    const value = new URL(url, apiBase);
    return value.origin === new URL(apiBase).origin ? value : undefined;
  } catch {
    return undefined;
  }
}

export function prepareAccessRequest(config: any, apiBase: string) {
  let url: URL | undefined;
  try {
    url = applicationUrl(new URL(config.url || '', config.baseURL || apiBase).href, apiBase);
  } catch {
    return config;
  }
  if (!url) return config;
  if (/^\/user\/(login|register|google-auth|microsoft-auth)$/.test(url.pathname))
    advanceAccessSession();
  return { ...config, __accessGeneration: generation };
}

export function isAccountDisabledError(error: any): boolean {
  return error?.response?.status === 401 && error.response.data?.code === 'ACCOUNT_DISABLED';
}

export function handleAccessFailure(error: any): void {
  if (error?.response?.status === 403 && error.response.data?.code === 'SUBSCRIPTION_REQUIRED' &&
      error.config?.__accessGeneration === generation && !disabled) {
    setSubscriptionAccess({ ...error.response.data, allowed: false });
    return;
  }
  if (
    !isAccountDisabledError(error) ||
    error.config?.__accessGeneration !== generation ||
    disabled
  )
    return;
  disabled = true;
  generation++;
  analytics.reset();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ACCOUNT_DISABLED_EVENT));
  if (
    typeof window !== 'undefined' &&
    (window.location.pathname !== '/user/login' ||
      new URLSearchParams(window.location.search).get('reason') !== 'account-disabled')
  ) {
    navigate('/user/login?reason=account-disabled');
  }
}

/** Handles rejected streaming requests without buffering successful streams. */
export function createAccessFetch(apiBase: string, transport: typeof fetch): typeof fetch {
  return async (input, init) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const config = prepareAccessRequest({ url }, apiBase);
    const response = await transport(input, init);
    if (config.__accessGeneration !== undefined && (response.status === 401 || response.status === 403)) {
      try {
        const data = await response.clone().json();
        handleAccessFailure({ config, response: { status: response.status, data } });
      } catch {
        /* Preserve non-JSON failures and the original response body. */
      }
    }
    if (response.status >= 200 && response.status < 300) assertCurrentAccessResponse({ config });
    return response;
  };
}
